<script setup lang="ts">
/**
 * **Area 树的递归渲染器**。
 *
 * 一棵树，两种节点：
 *   · `split` → flex 容器 + 分隔条 + 两个递归的 `NpAreaTree`
 *   · `area`  → `NpArea`（叶子）
 *
 * 所有插槽（`headerExtra` / `panel` / `editor`）原样往下透传，
 * 由叶子上的 `NpArea` 调用。于是 Playground 只写一次插槽内容，
 * 不管树有几层、几个 Area。
 *
 * ## ⚠️ 两条「不能优化掉」的规则
 *
 * **1. 隐藏 Area 必须用 `v-show`，不能用 `v-if`。**
 * 最大化 / 切换模式栏时，其它 Area 只是「看不见」，**不能卸载**。
 * 卸载会销毁里面的 VueFlow 实例，视口、选中、折叠状态全丢
 * （`npm run verify:areas` 专门守这一条 —— 用 `v-if` 的话节点数断言照样过，
 * 只有「DOM 引用不变」那条会挂）。
 *
 * **2. 递归必须有 `:key`，而且是稳定的 id。**
 * 用数组下标当 key 的话，关掉一个 Area 会让后面所有 Area 的 key 前移 →
 * Vue 认为「内容变了」→ 重建组件 → 同样丢状态。所以 key 一律用 `node.id`。
 */
import NpArea from './NpArea.vue'
import NpSplitHandle from './NpSplitHandle.vue'
import { findArea } from './areas'
import type { AreaLeaf, AreaNode, EditorType, PanelSide, RegionKey } from './areas'

const props = defineProps<{
  node: AreaNode
  /** 被最大化的 Area id；`null` = 正常分屏 */
  maximizedId: string | null
  focusedId: string
  areaCount: number
  /**
   * Area id → 上次记下的滚动位置。
   *
   * ⚠️ 由宿主保管、`NpArea` 自己恢复（只有组件知道自己的主区域是哪个元素）。
   * 树只负责**按 id 取出来往下传** —— 它是纯布局组件，不该持有这份状态。
   */
  scrollMarks?: Record<string, { selector: string; top: number; left: number }>
}>()

const emit = defineEmits<{
  resize: [splitId: string, ratio: number]
  focus: [areaId: string]
  setEditor: [areaId: string, editor: EditorType]
  toggleRegion: [areaId: string, key: RegionKey]
  /** 某一侧的侧栏换成哪一页（`null` = 收起） */
  setPanel: [areaId: string, side: 'left' | 'right', tab: string | null]
  split: [areaId: string, dir: 'row' | 'col']
  close: [areaId: string]
  toggleMaximize: [areaId: string]
  /** 右键 Area 头（宿主据此开 Area 菜单） */
  areaMenu: [areaId: string, event: MouseEvent]
  /** 点了窗口类型按钮（宿主据此开「换窗口类型」菜单） */
  editorMenu: [areaId: string, anchor: HTMLElement | null]
  /** 侧栏开合（宿主据此让图引擎重新量一次） */
  panelToggled: [areaId: string]
  /**
   * 拖分隔条**拖过头** → 把这一侧的 Area 并掉。
   *
   * ⚠️ 树只转发，**不在这里判能不能合并** —— 它只有自己那棵子树，
   * 判不了「是不是最后一个 Area」。那件事在宿主的 `joinTarget()` 里。
   */
  join: [areaId: string]
  /** 从分割把手拖出来的分割，边界落在松手位置 */
  splitAt: [areaId: string, dir: 'row' | 'col', ratio: number]
  /** 某个 Area 里的滚动容器动了 */
  scroll: [areaId: string, mark: { selector: string; top: number; left: number }]
}>()

/**
 * 插槽声明。
 *
 * ⚠️ **必须写出来**，不能靠推断。少了它，模板里的
 * `<template #toolbar="{ area }">` 拿不到 `area` 的类型，而紧接着
 * `<slot name="toolbar" :area="area" />` 又把这个未定类型往外传 →
 * `vue-tsc` 判定成循环依赖，报 TS7022（`'area' implicitly has type 'any'
 * because it is referenced directly or indirectly in its own initializer`）。
 * 声明之后类型有了确定的来源，循环断开。
 *
 * 四种插槽的 props 形状完全一样（`{ area }`）—— 这是有意的：
 * 内容是「这个 Area 放什么」，而决定放什么的是**宿主**（它持有状态），
 * 所以组件只把 Area 交出去，不做任何 `v-if editor === 'graph'` 之类的判断。
 */
defineSlots<{
  /** 这个窗口自己的 header 内容（见 `NpArea` 的 `headerExtra` 插槽） */
  headerExtra?: (props: { area: AreaLeaf }) => unknown
  /** 侧栏面板内容，按 `(area, side, tab)` 分派 */
  panel?: (props: { area: AreaLeaf; side: PanelSide; tab: string }) => unknown
  /**
   * toolbar Region 的内容。
   *
   * ⚠️ 内置的左侧工具栏**已经删掉**（实测要求），但**这个 Region 还在** ——
   * 它是窗口的一个可选区块，主题可以决定它停靠在哪条边、要不要浮起来
   * （见 `regionPlacement.ts`）。内置内容没了不等于这个扩展点没了。
   */
  toolbar?: (props: { area: AreaLeaf }) => unknown
  editor?: (props: { area: AreaLeaf }) => unknown
}>()


/**
 * 这一侧整体合并时要删掉的 Area id（`null` = 不能合并）。
 *
 * ⚠️ 树**没有**布局树，只有自己这一棵子树。所以它判不了
 * 「是不是最后一个 Area」—— 那个判断留给宿主的 `joinTarget()` 兜底。
 *
 * 这里只做**结构上判得了**的那一半：这一侧是不是单个 Area。
 * 判成 `null` 时手柄根本不会进合并区，也就不会发出 `join`。
 */
function joinTargetOf(side: 'a' | 'b'): string | null {
  if (props.node.kind !== 'split') return null
  const branch = side === 'a' ? props.node.a : props.node.b
  return branch.kind === 'area' ? branch.id : null
}

/**
 * 这个 Area 现在该不该显示。
 *
 * 有最大化时只有被最大化的那个显示；没有最大化时全显示。
 * ⚠️ 返回的是 `v-show` 的值（不是 `v-if`）—— 见文件头注释规则 1。
 */
function visible(area: AreaLeaf): boolean {
  return props.maximizedId === null || props.maximizedId === area.id
}

/**
 * 这棵子树里**有没有**被最大化的那个 Area。
 *
 * ⚠️ 为什么需要它：最大化时 `NpArea` 只是被 `v-show` 藏起来，
 * 而**包着它的 `.np-split__pane` 还在**，并且它的 `flex-basis` 是按
 * 分屏比例写死的。于是：
 *
 *   手机档（上下分，ratio 0.68）最大化「输出」→
 *   上面那个 pane 仍然占 68% 高度（内容 `display:none`，但那 68% 还在），
 *   输出只能挤在下面的 32% 里。
 *
 * 用户实测反馈正是这个：「输出的窗口不是占用中间全部大小，只有下边一部分」。
 *
 * 修法是让 pane 的显隐**跟着子树走**：不含最大化 Area 的那一侧整个
 * `v-show` 掉（`display: none` 会把它从 flex 布局里摘出去），
 * 含的那一侧改用 `flex: 1 1 auto` 吃掉全部空间。
 *
 * ⚠️ 是 `v-show` 不是 `v-if` —— 把 pane 卸载会连带把里面的 VueFlow 销毁，
 * 视口/选中全丢（这正是文件头规则 1 说的那件事，只是这次发生在
 * pane 这一层而不是 Area 那一层）。
 */
function subtreeVisible(child: AreaNode): boolean {
  return props.maximizedId === null || findArea(child, props.maximizedId) !== null
}

/**
 * `a` 侧的伸缩参数。
 *
 * 分屏时：`flex-grow: 0` + `flex-basis: calc(ratio% - 半个分隔条)`，
 * 让 `a` 拿固定的一份、`b`（`flex: 1 1 0`）吃掉剩下的 ——
 * 两边各让出半个分隔条，比例才对称（不然 `a` 会比 `ratio` 宽一点，
 * 拖到 50% 时两侧并不等宽）。
 *
 * ⚠️ 最大化时**不能用比例**：含最大化 Area 的那一侧要吃满，
 * 否则它还是按 ratio 分到一块（见 `subtreeVisible` 的注释）。
 *
 * ⚠️ 用**长写属性**（`flex-grow` / `flex-basis`）而不是 `flex` 简写：
 *   · 简写会把没写的子属性重置成初始值，容易在条件分支之间互相干扰；
 *   · 更实际的原因：测试要断言「最大化时 grow 变成 1」，
 *     而 `flex-basis` 的值是 `calc(68% - var(--np-split-half, 3px))` ——
 *     happy-dom 的 CSS 解析器**存不下 `calc(var(...))`**，会整条丢掉，
 *     于是 `el.style.flex` 读出来是空的，测试没法区分两种状态。
 *     拆成长写之后 `flex-grow` 是个纯数字，稳定可读。
 */
function paneStyleA(ratio: number): Record<string, string> {
  if (props.maximizedId !== null) {
    return { flexGrow: '1', flexShrink: '1', flexBasis: 'auto' }
  }
  return {
    flexGrow: '0',
    flexShrink: '0',
    flexBasis: `calc(${(ratio * 100).toFixed(4)}% - var(--np-split-half, 3px))`,
  }
}
</script>

<template>
  <!-- 叶子：一个 Area -->
  <!--
    ⚠️⚠️ `:key` **必须给**，而且必须是 Area 自己的 id。
    没有它的时候 Vue 会**复用组件实例**：新建窗口（分屏）之后，
    新 Area 的 `NpArea` 可能沿用第一个 Area 的那个实例，于是它的
    **组件内状态被一起继承** —— `editorMenuOpen` 已经是 `true`，
    点一下变成 `false`，`emit('editorMenu')` 就不发了。

    实测反馈正是这个：「创建新的窗口后，左上角的类型不能改，
    只有第一个的左上角可以改」。

    ⚠️ 同一个原因也会让 `openPanel`（侧栏开着哪一页）、`lastTabOfSide`、
    滚动位置这些**局部状态在 Area 之间串**。所以这不是"补个 key 让它别报警告"，
    是**状态归属**问题：一个 Area 的界面状态必须跟着那个 Area 走。
  -->
  <NpArea
    v-if="node.kind === 'area'"
    :key="node.id"
    v-show="visible(node)"
    :area="node"
    :area-count="areaCount"
    :focused="focusedId === node.id"
    :maximized="maximizedId === node.id"
    :scroll-mark="scrollMarks?.[node.id] ?? null"
    @focus="emit('focus', node.id)"
    @area-menu="(ev: MouseEvent) => emit('areaMenu', node.id, ev)"
    @scroll="(mark) => emit('scroll', node.id, mark)"
    @set-editor="(editor: EditorType) => emit('setEditor', node.id, editor)"
    @toggle-region="(key: RegionKey) => emit('toggleRegion', node.id, key)"
    @set-panel="(side: 'left' | 'right', tab: string | null) => emit('setPanel', node.id, side, tab)"
    @split="(dir: 'row' | 'col') => emit('split', node.id, dir)"
    @split-at="(dir: 'row' | 'col', ratio: number) => emit('splitAt', node.id, dir, ratio)"
    @close="emit('close', node.id)"
    @toggle-maximize="emit('toggleMaximize', node.id)"
    @editor-menu="(anchor: HTMLElement | null) => emit('editorMenu', node.id, anchor)"
    @panel-toggled="emit('panelToggled', node.id)"
  >
    <!--
      插槽原样透传：Playground 只写一次，不管树有几层。
      ⚠️ 显式写 `:area="area"` 而不是 `v-bind="slotProps"` ——
      后者会让 vue-tsc 把 slotProps 推断成循环依赖（TS7022）。
      NpArea 透给插槽的本来就只有 `{ area }`，写出来更清楚。
    -->
    <template #headerExtra="{ area }"><slot name="headerExtra" :area="area" /></template>
    <template #panel="p"><slot name="panel" v-bind="p" /></template>
    <template #toolbar="{ area }"><slot name="toolbar" :area="area" /></template>
    <template #editor="{ area }"><slot name="editor" :area="area" /></template>
  </NpArea>

  <!-- 内部节点：一次切分 -->
  <div
    v-else
    class="np-split"
    :class="`np-split--${node.dir}`"
  >
    <div
      v-show="subtreeVisible(node.a)"
      class="np-split__pane js-split-pane-a"
      :style="paneStyleA(node.ratio)"
    >
      <NpAreaTree
        :key="node.a.id"
        :node="node.a"
        :maximized-id="maximizedId"
        :focused-id="focusedId"
        :area-count="areaCount"
        :scroll-marks="scrollMarks"
            @resize="(id, r) => emit('resize', id, r)"
        @focus="(id) => emit('focus', id)"
        @area-menu="(id, ev) => emit('areaMenu', id, ev)"
        @editor-menu="(id, anchor) => emit('editorMenu', id, anchor)"
        @panel-toggled="(id) => emit('panelToggled', id)"
        @scroll="(id, mark) => emit('scroll', id, mark)"
        @set-editor="(id, e) => emit('setEditor', id, e)"
        @toggle-region="(id, k) => emit('toggleRegion', id, k)"
        @set-panel="(id, side, tab) => emit('setPanel', id, side, tab)"
        @split="(id, d) => emit('split', id, d)"
        @split-at="(id, d, r) => emit('splitAt', id, d, r)"
        @join="(id) => emit('join', id)"
        @close="(id) => emit('close', id)"
        @toggle-maximize="(id) => emit('toggleMaximize', id)"
      >
        <template #headerExtra="{ area }"><slot name="headerExtra" :area="area" /></template>
        <template #panel="p"><slot name="panel" v-bind="p" /></template>
        <template #toolbar="{ area }"><slot name="toolbar" :area="area" /></template>
        <template #editor="{ area }"><slot name="editor" :area="area" /></template>
      </NpAreaTree>
    </div>

    <NpSplitHandle
      v-if="maximizedId === null"
      :dir="node.dir"
      :data-ratio="node.ratio"
      :join-a="joinTargetOf('a')"
      :join-b="joinTargetOf('b')"
      @resize="(ratio: number) => emit('resize', node.id, ratio)"
      @join="(areaId: string) => emit('join', areaId)"
    />

    <div v-show="subtreeVisible(node.b)" class="np-split__pane np-split__pane--b">
      <NpAreaTree
        :key="node.b.id"
        :node="node.b"
        :maximized-id="maximizedId"
        :focused-id="focusedId"
        :area-count="areaCount"
        :scroll-marks="scrollMarks"
            @resize="(id, r) => emit('resize', id, r)"
        @focus="(id) => emit('focus', id)"
        @area-menu="(id, ev) => emit('areaMenu', id, ev)"
        @editor-menu="(id, anchor) => emit('editorMenu', id, anchor)"
        @panel-toggled="(id) => emit('panelToggled', id)"
        @scroll="(id, mark) => emit('scroll', id, mark)"
        @set-editor="(id, e) => emit('setEditor', id, e)"
        @toggle-region="(id, k) => emit('toggleRegion', id, k)"
        @set-panel="(id, side, tab) => emit('setPanel', id, side, tab)"
        @split="(id, d) => emit('split', id, d)"
        @split-at="(id, d, r) => emit('splitAt', id, d, r)"
        @join="(id) => emit('join', id)"
        @close="(id) => emit('close', id)"
        @toggle-maximize="(id) => emit('toggleMaximize', id)"
      >
        <template #headerExtra="{ area }"><slot name="headerExtra" :area="area" /></template>
        <template #panel="p"><slot name="panel" v-bind="p" /></template>
        <template #toolbar="{ area }"><slot name="toolbar" :area="area" /></template>
        <template #editor="{ area }"><slot name="editor" :area="area" /></template>
      </NpAreaTree>
    </div>
  </div>
</template>

<style scoped>
.np-split {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  /*
   * 分隔条宽度在这里定义**一次**：`NpSplitHandle` 读它当自己的
   * flex-basis，两边的 `paneFlexA` 读它的**一半**做补偿。
   * 两边必须来自同一个值 —— 否则拖到 50% 时两侧并不等宽。
   * （`NpSplitHandle` 的 scoped 样式里也有一份同名变量，那是它自己的
   *   视觉宽度；触摸端两边都由 `(pointer: coarse)` 放大到 16px。）
   */
  /*
   * ⚠️ 视觉宽度。用户实测反馈「窗口之间的调整分割有点宽」——
   * 6px 在深色界面上是一条很显眼的带子，压到 4px 更像一条缝。
   *
   * 注意「看得见的宽度」和「点得到的宽度」是两件事：
   * `NpSplitHandle` 用 `inset: calc(var(--np-split-size) / -2)` 把命中区
   * 向外撑开（触摸端靠 `(pointer: coarse)` 的 16px 覆盖），
   * 所以变窄**不会**让鼠标更难抓。
   */
  /*
   * ⚠️ 本地变量叫 `--np-split-w`，**不是** `--np-split-size`。
   *
   * 主题通过 `--np-split-size` 声明想要的厚度（见 `theme/derive`），
   * 而元素**自己声明的变量会盖掉继承值** —— 本地也叫那个名字的话，
   * 主题设了也没用（“设了不生效”这类静默失败这个仓库记过好几条，
   * 端口色那次就是这么栽的）。
   */
  /*
   * ⚠️ 这里有两个**必须分开**的变量，混在一起出过 bug：
   *
   *   --np-split-w     两个窗口之间那道**缝有多宽**。它进 flex 布局
   *                    （`flex: 0 0 var(--np-split-w)`），两侧 pane 的
   *                    `flex-basis` 还要减掉它的一半。**恒为 2px**。
   *
   *   --np-split-grab  命中区向外**各撑开多少**。只影响 `::before`，
   *                    不进布局。触摸端要 9px（手指），桌面 3px（鼠标）。
   *
   * 原来只有一个 `--np-split-w`，触摸端被放大到 12px —— 那时它既是
   * **缝宽**又是命中区。分割条还是实心颜色时看不出来，等它改成透明
   * 之后，那 12px 就变成了一条**透出底色的宽带子**
   * （实测反馈：「--np-split-size 未定义」+ 缝很粗）。
   *
   * 换句话说：**命中区可以随便撑大，缝宽不行** —— 前者不参与布局，
   * 后者参与。这就是为什么它们得是两个变量。
   */
  --np-split-w: var(--np-split-size, 2px);
  --np-split-half: calc(var(--np-split-w) / 2);
  --np-split-grab: 3px;
}

.np-split--row {
  flex-direction: row;
}

.np-split--col {
  flex-direction: column;
}

.np-split__pane {
  display: flex;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

/*
 * ⚠️ `b` 是 `flex: 1 1 0`（在模板里由 CSS 给，不在 inline style 里）——
 * 这样 `a` 用 `flex-basis` 定宽度、`b` 吃掉剩下的，两者加起来永远
 * 正好填满容器（不会出现「拖到 88% 之后右侧还剩一条缝」）。
 */
.np-split__pane--b {
  flex: 1 1 0;
}

@media (pointer: coarse) {
  .np-split {
    /*
     * 触摸端仍然留宽一些的**命中区**，但没有 16px 那么夸张 ——
     * 那在手机上等于凭空少了 16px 高度。12px 配合撑开的命中区够用。
     */
    /*
     * ⚠️ 触摸端放大的是**命中区**，不是缝宽。
     * 缝宽恒为 2px（主题可以改），放大它只会在两个窗口之间留一条
     * 透出底色的空带 —— 那看起来像渲染坏了，不像分隔条。
     *
     * 9px × 2 + 2px = **20px 的命中区**（`::before` 向外各撑开 9px），
     * 这个宽度手指够用了；而视觉上两栏还是紧挨着的。
     */
    --np-split-grab: 9px;
  }
}
</style>
